import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Form,
  Button,
  Alert,
  Table,
  Image,
  Spinner,
  Badge
} from "react-bootstrap";
import { FaCalendarAlt, FaClock, FaMoneyBillWave, FaCommentDots, FaUserTie, FaSearch } from "react-icons/fa";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_orange.css";
import moment from "moment";
import { adsQueueService, dashboardService } from "../../services/api";

const BookingService = () => {
  const [formData, setFormData] = useState({
    date: new Date(),
    hours: "1",
    serviceType: "live",
    budget: "",
    specialRequirements: ""
  });
  
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [validated, setValidated] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageId, setPageId] = useState(null);
  
  const [pageData, setPageData] = useState(null);
  
  const [bookingHistory, setBookingHistory] = useState([]);

  useEffect(() => {
    fetchMemberProfile();
    fetchBookingHistory();
  }, []);

  useEffect(() => {
    fetchBookingHistory();
  }, [selectedMonth]);

  const fetchMemberProfile = async () => {
    try {
      setIsLoading(true);
      const response = await dashboardService.getPages();
      
      if (Array.isArray(response) && response.length > 0) {
        const page = response[0];
        console.log('Page data received:', page);
        setPageData(page);
        const extractedPageId = page._id;
        if (extractedPageId) {
          setPageId(extractedPageId);
          console.log('Page ID set to:', extractedPageId);
        } else {
          console.warn('No page ID found in page object:', page);
        }
      } else if (response && response.success && response.data && response.data.pages) {
        if (response.data.pages.length > 0) {
          const page = response.data.pages[0];
          console.log('Page data received:', page);
          setPageData(page);
          const extractedPageId = page._id;
          if (extractedPageId) {
            setPageId(extractedPageId);
            console.log('Page ID set to:', extractedPageId);
          } else {
            console.warn('No page ID found in page object:', page);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching member profile:", error);
      setAlertVariant("warning");
      setAlertMessage("ไม่สามารถโหลดข้อมูลเพจได้ กรุณาลองใหม่อีกครั้ง");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBookingHistory = async () => {
    try {
      const startDate = moment(selectedMonth).startOf('month').toISOString();
      const endDate = moment(selectedMonth).endOf('month').toISOString();
      
      const response = await adsQueueService.getQueueEntries({
        page: 1,
        limit: 100,
        sortBy: 'scheduledDate',
        sortOrder: 'desc',
        startDate: startDate,
        endDate: endDate
      });
      
      const tasksData = response.tasks || response.data || [];
      if (Array.isArray(tasksData)) {
        const formattedHistory = tasksData.map(entry => ({
          id: entry._id,
          createdAt: moment(entry.createdAt).format("YYYY-MM-DD HH:mm"),
          serviceDate: moment(entry.scheduledDate).format("YYYY-MM-DD HH:mm"),
          hours: entry.durationHours || entry.adContent?.hours || 1,
          type: entry.serviceType === "live" ? "Live" : entry.serviceType === "post" ? "Post" : entry.taskType === "live" ? "Live" : entry.taskType === "post" ? "Post" : "Consult",
          status: entry.status === "pending" ? "รอยืนยัน" : entry.status === "wait_assign" ? "รอมอบหมาย" : entry.status === "assigned" ? "มอบหมายแล้ว" : entry.status === "in_progress" ? "กำลังดำเนิน" : entry.status === "completed" ? "เสร็จสิ้น" : entry.status === "canceled" ? "ยกเลิก" : "รอยืนยัน",
          budget: entry.adsBudget || entry.adContent?.budget || "0"
        }));
        setBookingHistory(formattedHistory);
      }
    } catch (error) {
      console.error("Error fetching booking history:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date: date[0]
    });
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const getFilteredBookings = () => {
    return bookingHistory.filter(booking => {
      const bookingMonth = moment(booking.serviceDate).format("YYYY-MM");
      return bookingMonth === selectedMonth;
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "รอยืนยัน":
        return "bg-warning text-dark";
      case "รอรับบริการ":
        return "bg-info";
      case "เสร็จสิ้น":
        return "bg-success";
      case "ยกเลิก":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!pageId) {
      setAlertVariant("danger");
      setAlertMessage("ไม่พบข้อมูลเพจ กรุณาเชื่อมต่อเพจ Facebook ของคุณก่อน");
      setShowAlert(true);
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const queueData = {
        pageId: pageId,
        serviceType: formData.serviceType,
        scheduledDate: moment(formData.date).toISOString(),
        durationHours: parseInt(formData.hours),
        adsBudget: parseFloat(formData.budget),
        specialRequirements: formData.specialRequirements || ""
      };
      
      const response = await adsQueueService.createQueueEntry(queueData);
      
      if (response.data) {
        const responseData = response.data.data || response.data;
        const newBooking = {
          id: responseData._id,
          createdAt: moment(responseData.createdAt).format("YYYY-MM-DD HH:mm"),
          serviceDate: moment(responseData.scheduledDate).format("YYYY-MM-DD HH:mm"),
          hours: responseData.durationHours || parseInt(formData.hours),
          type: formData.serviceType === "live" ? "Live" : formData.serviceType === "post" ? "Post" : "Consult",
          status: "รอยืนยัน",
          budget: responseData.adsBudget || formData.budget
        };
        
        setBookingHistory([newBooking, ...bookingHistory]);
        
        setAlertVariant("success");
        setAlertMessage("จองคิว Boost Ads สำเร็จ! กรุณารอการยืนยันจากเจ้าหน้าที่");
        setShowAlert(true);
        
        setFormData({
          date: new Date(),
          hours: "1",
          serviceType: "live",
          budget: "",
          specialRequirements: ""
        });
        setValidated(false);
        
        setTimeout(() => {
          setShowAlert(false);
        }, 5000);
      }
    } catch (error) {
      console.error("Error creating queue entry:", error);
      setAlertVariant("danger");
      
      if (error.response) {
        if (error.response.status === 403) {
          setAlertMessage("คุณไม่มีสิทธิ์เข้าถึงเพจนี้");
        } else if (error.response.status === 404) {
          setAlertMessage("ไม่พบข้อมูลสมาชิกหรือเพจ");
        } else if (error.response.status === 400) {
          setAlertMessage("ข้อมูลไม่ครบถ้วน กรุณาตรวจสอบและลองใหม่อีกครั้ง");
        } else {
          setAlertMessage(error.response.data?.message || "เกิดข้อผิดพลาดในการจองคิว กรุณาลองใหม่อีกครั้ง");
        }
      } else {
        setAlertMessage("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต");
      }
      
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div id="content-page" className="content-inner">
        <Container>
          <Row className="row-eq-height">
            <Col md="12">
              <Row className="mb-5">
                <Col md="12">
                  <div className="card-header d-flex justify-content-between">
                    <div className="header-title">
                      <h4 className="card-title">จองคิว Boost Ads</h4>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {showAlert && (
                <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                  {alertMessage}
                </Alert>
              )}
              
              {/* Page Info Section */}
              <Row className="mb-4">
                <Col md="12">
                  <Card>
                    <Card.Header>
                      <h5 className="card-title mb-0">ข้อมูลเพจ</h5>
                    </Card.Header>
                    <Card.Body className="py-3">
                      {pageData ? (
                        <div>
                          <h5 className="mb-2">{pageData.pageName}</h5>
                          <p className="text-muted mb-2 small">
                            <strong>หมวดหมู่:</strong> {pageData.category || 'ไม่ระบุ'}
                          </p>
                          <p className="text-muted mb-2 small">
                            <strong>ชื่อบัญชี:</strong> {pageData.accountName || 'ไม่ระบุ'}
                          </p>
                          <p className="text-muted mb-2 small">
                            <strong>ลูกค้า:</strong> {pageData.customerName || 'ไม่ระบุ'}
                          </p>
                          <p className="text-muted mb-2 small">
                            <strong>เบอร์โทร:</strong> {pageData.telephone || 'ไม่ระบุ'}
                          </p>
                          {pageData.pageLink && (
                            <p className="mb-2">
                              <a href={pageData.pageLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                ดูเพจ Facebook
                              </a>
                            </p>
                          )}
                          <div className="mt-2">
                            <Badge bg={pageData.activeStatus ? "success" : "secondary"} className="me-2">
                              {pageData.activeStatus ? 'ใช้งานอยู่' : 'ไม่ได้ใช้งาน'}
                            </Badge>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted mb-3">ยังไม่มีเพจที่เชื่อมต่อ</p>
                          <Button variant="primary" size="sm">
                            เชื่อมต่อเพจ Facebook
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              {/* Booking Form Section */}
              <Row className="mb-4">
                <Col md="12">
                  <Card>
                    <Card.Header>
                      <h4 className="card-title">ฟอร์มจองคิว</h4>
                    </Card.Header>
                    <Card.Body>
                      <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Row>
                          <Col md="6">
                            <Form.Group className="mb-3" controlId="formDate">
                              <Form.Label>
                                <FaCalendarAlt className="me-2" />
                                วันและเวลา
                              </Form.Label>
                              <Flatpickr
                                className="form-control"
                                value={formData.date}
                                onChange={handleDateChange}
                                options={{
                                  enableTime: true,
                                  dateFormat: "Y-m-d H:i",
                                  minDate: "today",
                                  time_24hr: true
                                }}
                                required
                              />
                              <Form.Control.Feedback type="invalid">
                                กรุณาเลือกวันและเวลา
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          
                          <Col md="6">
                            <Form.Group className="mb-3" controlId="formHours">
                              <Form.Label>
                                <FaClock className="me-2" />
                                จำนวนชั่วโมง
                              </Form.Label>
                              <Form.Select
                                name="hours"
                                value={formData.hours}
                                onChange={handleChange}
                                required
                              >
                                <option value="1">1 ชั่วโมง</option>
                                <option value="2">2 ชั่วโมง</option>
                                <option value="3">3 ชั่วโมง</option>
                                <option value="4">4 ชั่วโมง</option>
                                <option value="5">5 ชั่วโมง</option>
                                <option value="6">6 ชั่วโมง</option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                กรุณาเลือกจำนวนชั่วโมง
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md="6">
                            <Form.Group className="mb-3">
                              <Form.Label>ประเภทบริการ</Form.Label>
                              <div>
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="Boost Live"
                                  name="serviceType"
                                  value="live"
                                  checked={formData.serviceType === "live"}
                                  onChange={handleChange}
                                  id="service-live"
                                />
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="Boost Post"
                                  name="serviceType"
                                  value="post"
                                  checked={formData.serviceType === "post"}
                                  onChange={handleChange}
                                  id="service-post"
                                />
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="Consult"
                                  name="serviceType"
                                  value="consult"
                                  checked={formData.serviceType === "consult"}
                                  onChange={handleChange}
                                  id="service-consult"
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          
                          <Col md="6">
                            <Form.Group className="mb-3" controlId="formBudget">
                              <Form.Label>
                                <FaMoneyBillWave className="me-2" />
                                งบโฆษณา (บาท)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                placeholder="ระบุงบโฆษณา"
                                name="budget"
                                value={formData.budget}
                                onChange={handleChange}
                                required
                              />
                              <Form.Control.Feedback type="invalid">
                                กรุณาระบุงบโฆษณา
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Form.Group className="mb-3" controlId="formSpecialRequirements">
                          <Form.Label>
                            <FaCommentDots className="me-2" />
                            ความต้องการพิเศษ
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="ระบุความต้องการพิเศษ (ถ้ามี)"
                            name="specialRequirements"
                            value={formData.specialRequirements}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        
                        <Button 
                          variant="primary" 
                          type="submit" 
                          className="mt-2"
                          disabled={isSubmitting || !pageId}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              กำลังจองคิว...
                            </>
                          ) : (
                            "ยืนยันจองคิว"
                          )}
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              {/* Booking History Section */}
              <Row>
                <Col md="12">
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <h4 className="card-title mb-0">ประวัติการจอง</h4>
                      <div className="d-flex align-items-center">
                        <FaSearch className="me-2 text-muted" />
                        <Form.Control
                          type="month"
                          value={selectedMonth}
                          onChange={handleMonthChange}
                          style={{ width: 'auto' }}
                        />
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="table-responsive">
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>ประเภทบริการ</th>
                              <th>วันเวลาที่สร้าง</th>
                              <th>วันเวลาบริการ</th>
                              <th>จำนวนชั่วโมง</th>
                              <th>งบโฆษณา</th>
                              <th>สถานะการจอง</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredBookings().length > 0 ? (
                              getFilteredBookings().map((booking) => (
                                <tr key={booking.id}>
                                  <td>{booking.type}</td>
                                  <td>{booking.createdAt}</td>
                                  <td>{booking.serviceDate}</td>
                                  <td>{booking.hours} ชม.</td>
                                  <td>{booking.budget} บาท</td>
                                  <td>
                                    <span className={`badge ${getStatusBadgeClass(booking.status)}`}>
                                      {booking.status}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">
                                  ไม่พบข้อมูลการจองในเดือนที่เลือก
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default BookingService;
